import React, { useState, useEffect } from 'react';
import { useTransactions } from '../hooks/useTransactions';
import { useWallet } from '../hooks/useWallet';
import { format } from 'date-fns';
import { PublicKey } from '@solana/web3.js';

// Format token amounts with proper decimal places
const formatAmount = (amount, tokenMint) => {
  // Default to 9 decimals for unknown tokens
  const decimals = tokenMint === 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' ? 6 : 9;
  return (amount / Math.pow(10, decimals)).toFixed(decimals);
};

// Get token symbol from mint address
const getTokenSymbol = (mint) => {
  const tokens = {
    'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 'USDC',
    'Es9vMFrzaCERBbMERCj2t5Ju6u2RZkMnDNtDG8oYPka': 'USDT',
    'So11111111111111111111111111111111111111112': 'SOL'
  };
  return tokens[mint] || mint.slice(0, 4) + '...' + mint.slice(-4);
};

// Format wallet address for display
const formatAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

export default function TransactionHistory() {
  const { wallet } = useWallet();
  const walletAddress = wallet?.address;
  
  const {
    transactions,
    loading,
    error,
    fetchTransactions,
    loadMoreTransactions,
    pagination
  } = useTransactions();

  // Fetch transactions when wallet address changes
  useEffect(() => {
    if (walletAddress) {
      fetchTransactions(walletAddress, { limit: 10 });
    }
  }, [walletAddress, fetchTransactions]);

  // Handle loading more transactions
  const handleLoadMore = () => {
    if (pagination.hasMore && !loading) {
      loadMoreTransactions(walletAddress, { limit: 10 });
    }
  };

  if (!walletAddress) {
    return (
      <div className="p-4 bg-white rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Transaction History</h2>
        <p className="text-gray-500">Please connect your wallet to view transaction history.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">Transaction History</h2>
      </div>
      
      {loading && !transactions.length ? (
        <div className="p-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading transactions...</p>
        </div>
      ) : error ? (
        <div className="p-4 text-red-500">
          <p>Error loading transactions: {error}</p>
          <button 
            onClick={() => fetchTransactions(walletAddress, { limit: 10 })}
            className="mt-2 px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm"
          >
            Retry
          </button>
        </div>
      ) : transactions.length === 0 ? (
        <div className="p-4 text-center text-gray-500">
          No transactions found
        </div>
      ) : (
        <>
          <div className="divide-y divide-gray-100">
            {transactions.map((tx) => (
              <div key={tx.tx_signature} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        tx.type === 'receive' ? 'bg-green-500' : 'bg-blue-500'
                      }`}></div>
                      <span className="font-medium capitalize">
                        {tx.type}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {format(new Date(tx.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                    {tx.destination_wallet && (
                      <div className="text-sm mt-1">
                        <span className="text-gray-500">To: </span>
                        <span className="font-mono">
                          {formatAddress(tx.destination_wallet)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={`text-right ${
                    tx.type === 'receive' ? 'text-green-600' : 'text-gray-900'
                  }`}>
                    <div className="font-medium">
                      {tx.type === 'receive' ? '+' : '-'}
                      {formatAmount(tx.amount, tx.token_mint)} {getTokenSymbol(tx.token_mint)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {tx.status}
                    </div>
                  </div>
                </div>
                {tx.metadata?.note && (
                  <div className="mt-2 text-sm text-gray-500">
                    {tx.metadata.note}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {pagination.hasMore && (
            <div className="p-4 border-t border-gray-100 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
